/**
 * @fileoverview Renovation Project Detail Page
 *
 * Universal detail page for renovation projects.
 * Uses UniversalDetailPage component with custom configuration and sections.
 *
 * Features:
 * - Project tasks kanban board
 * - Project information editing
 * - Project parts management
 * - Document management
 * - Email management
 * - Budget integration
 * - Unit assignment
 * - Photo/Document tabs in sidebar
 *
 * @module RenovationProject/DetailPage
 */

import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { UniversalDetailPage } from "shared/components/UniversalDetailPage";
import { RENOVATION_PROJECT_ENTITY_CONFIG } from "./config/renovationProjectEntityConfig";
import { ProjectPartsProvider } from "./contexts/ProjectPartsContext";

// Lazy load heavy custom sections to reduce initial bundle size and memory usage
// Only lazy-load sections that are below the fold or rarely used
const ProjectPartsSection = lazy(() =>
  import("./customSections/ProjectPartsSection").then((m) => ({
    default: m.ProjectPartsSection,
  })),
);
// ProjectDocumentsSection ("Project offertes, bestelbonnen en facturen") and
// ProjectEmailsSection are retired — the Financiële documenten section covers
// what users still need, and the inline email panel had no remaining use case.
const ProjectNotesSection = lazy(() =>
  import("./customSections/ProjectNotesSection").then((m) => ({
    default: m.ProjectNotesSection,
  })),
);
const BudgetLockSection = lazy(() =>
  import("./customSections/BudgetLockSection").then((m) => ({
    default: m.BudgetLockSection,
  })),
);
const UnifiedProjectPhotosSection = lazy(() =>
  import("./customSections/UnifiedProjectPhotosSection").then((m) => ({
    default: m.UnifiedProjectPhotosSection,
  })),
);
const PlanningLockSection = lazy(() =>
  import("./customSections/PlanningLockSection").then((m) => ({
    default: m.PlanningLockSection,
  })),
);
const ProjectUnitsOverviewSection = lazy(() =>
  import("./customSections/ProjectUnitsOverviewSection").then((m) => ({
    default: m.ProjectUnitsOverviewSection,
  })),
);
const ProjectFinancialDocumentsSection = lazy(() =>
  import("./customSections/ProjectFinancialDocumentsSection").then((m) => ({
    default: m.ProjectFinancialDocumentsSection,
  })),
);

// Modal components for full-screen photo view
import { PhotosModal } from "./components/PhotosModal";

// Keep kanban view eagerly loaded as it's critical UI
import { ProjectTaskKanbanView } from "./components/ProjectTaskKanbanView";
import { ProjectTaskDetailModal } from "./components/ProjectTaskDetailModal";
import { AddProjectTaskModal } from "./components/AddProjectTaskModal";
import { Image, FileText, X, Wrench } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import {
  ProjectTask,
  ProjectTaskStatus,
  RenovationProject,
} from "shared/types/renovation";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";

type MediaTab = "photos" | "documents" | "helpdesk";

// Memoized tab selector component for Photos/Documents/Helpdesk
const MediaTabSelector = React.memo<{
  activeTab: MediaTab;
  onTabChange: (tab: MediaTab) => void;
}>(({ activeTab, onTabChange }) => {
  return (
    <div className="border-neutral-primary/10 mb-4 overflow-hidden rounded-lg border bg-white shadow-sm">
      <div className="flex">
        <button
          onClick={() => onTabChange("photos")}
          className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 font-medium transition-colors ${
            activeTab === "photos"
              ? "bg-moss-primary text-white"
              : "text-neutral-secondary-mid hover:bg-neutral-50"
          }`}
        >
          <Image size={20} weight={activeTab === "photos" ? "fill" : "regular"} />
          <span>Foto's</span>
        </button>
        <button
          onClick={() => onTabChange("documents")}
          className={`border-neutral-primary/10 flex flex-1 items-center justify-center gap-2 border-l px-4 py-3 font-medium transition-colors ${
            activeTab === "documents"
              ? "bg-moss-primary text-white"
              : "text-neutral-secondary-mid hover:bg-neutral-50"
          }`}
        >
          <FileText size={20} weight={activeTab === "documents" ? "fill" : "regular"} />
          <span>Documenten</span>
        </button>
        <button
          onClick={() => onTabChange("helpdesk")}
          className={`border-neutral-primary/10 flex flex-1 items-center justify-center gap-2 border-l px-4 py-3 font-medium transition-colors ${
            activeTab === "helpdesk"
              ? "bg-moss-primary text-white"
              : "text-neutral-secondary-mid hover:bg-neutral-50"
          }`}
        >
          <Wrench size={20} weight={activeTab === "helpdesk" ? "fill" : "regular"} />
          <span>Helpdesk</span>
        </button>
      </div>
    </div>
  );
});

MediaTabSelector.displayName = "MediaTabSelector";

// Loading fallback for lazy-loaded sections
const SectionLoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center py-8">
    <div className="border-moss-primary/20 border-t-moss-primary h-6 w-6 animate-spin rounded-full border-3"></div>
    <span className="text-neutral-secondary-mid ml-3 text-sm">
      Sectie laden...
    </span>
  </div>
);

// Wrapper component for lazy-loaded sections with Suspense
const withLazySuspense = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  const WrappedComponent: React.FC<P> = (props) => (
    <Suspense fallback={<SectionLoadingFallback />}>
      <Component {...props} />
    </Suspense>
  );
  return WrappedComponent;
};

// Extracted stable component for Project Tasks Kanban
// Prevents re-renders when projectTasks changes
const ProjectTasksKanbanSection = React.memo<{
  entity?: any; // From UniversalDetailPage
  onUpdate?: (entity: any) => void; // From UniversalDetailPage
  projectId: number | null;
  projectTasks: ProjectTask[];
  isLoadingTasks: boolean;
  onTasksUpdate: () => void;
  onTaskClick: (task: ProjectTask) => void;
  onAddClick: (status: ProjectTaskStatus) => void;
  selectedPhases: string[];
  onPhaseToggle: (phase: string) => void;
  projectStatus?: string | null;
}>(
  ({
    projectId,
    projectTasks,
    isLoadingTasks,
    onTasksUpdate,
    onTaskClick,
    onAddClick,
    selectedPhases,
    onPhaseToggle,
  }) => {
    if (!projectId) return null;

    // Phase filter options - MEMOIZED (never changes)
    const phaseOptions = useMemo(
      () => [
        { value: "NONE", label: "Geen fase" }, // null projectPhase
        { value: "Ontwerp fase", label: "Ontwerp" },
        { value: "Gepland", label: "Uitvoeringsdossier" },
        { value: "In uitvoering", label: "In uitvoering - Werf" },
        { value: "Afgerond", label: "Voorlopige oplevering" },
        { value: "Volledig gefactureerd", label: "Definitieve oplevering" },
      ],
      [],
    );

    return (
      <div className="border-neutral-primary/20 overflow-hidden rounded-lg border bg-white shadow-sm">
        {/* Header */}
        <div className="border-neutral-primary/10 space-y-3 border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <h3 className="text-neutral-primary text-base font-semibold">
              Project Taken
            </h3>
            {projectTasks.length > 0 && (
              <span className="text-neutral-secondary-mid rounded-md bg-neutral-50 px-2 py-1 text-xs">
                {projectTasks.length}{" "}
                {projectTasks.length === 1 ? "taak" : "taken"}
              </span>
            )}
          </div>

          {/* Phase Filter - Chips for selected, Labels for unselected */}
          <div className="space-y-2">
            <label className="text-neutral-secondary-mid text-sm font-medium">
              Fase filter:
            </label>

            {/* Selected phases as chips with X button */}
            <div className="flex flex-wrap gap-2">
              {selectedPhases.map((phase) => {
                const option = phaseOptions.find((opt) => opt.value === phase);
                if (!option) return null;
                return (
                  <Badge
                    key={phase}
                    variant="default"
                    className="bg-moss-primary hover:bg-moss-primary/90 flex cursor-pointer items-center gap-1.5 px-3 py-1 text-white"
                    onClick={() => onPhaseToggle(phase)}
                    data-phase={phase}
                  >
                    <span>{option.label}</span>
                    <X size={14} weight="bold" />
                  </Badge>
                );
              })}
            </div>

            {/* Unselected phases as clickable labels */}
            <div className="flex flex-wrap gap-2">
              {phaseOptions
                .filter((option) => !selectedPhases.includes(option.value))
                .map((option) => (
                  <Badge
                    key={option.value}
                    variant="outline"
                    className="border-neutral-primary/20 text-neutral-secondary-mid hover:text-neutral-primary cursor-pointer px-3 py-1 hover:bg-neutral-50"
                    onClick={() => onPhaseToggle(option.value)}
                    data-phase={option.value}
                  >
                    {option.label}
                  </Badge>
                ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoadingTasks ? (
            <div className="flex items-center justify-center py-12">
              <div className="border-moss-primary/20 border-t-moss-primary h-8 w-8 animate-spin rounded-full border-4"></div>
              <span className="text-neutral-secondary-mid ml-3">
                Taken laden...
              </span>
            </div>
          ) : (
            <ProjectTaskKanbanView
              tasks={projectTasks}
              projectId={projectId}
              onUpdate={onTasksUpdate}
              onTaskClick={onTaskClick}
              onAddClick={onAddClick}
            />
          )}
        </div>
      </div>
    );
  },
);

ProjectTasksKanbanSection.displayName = "ProjectTasksKanbanSection";

export const RenovationProjectDetailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useSession();
  const buildingId = searchParams.get("buildingId");

  // Photos modal state - full-screen view instead of sidebar
  const [showPhotosModal, setShowPhotosModal] = useState(false);

  // Project tasks state
  const [projectTasks, setProjectTasks] = useState<ProjectTask[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  // PERFORMANCE: Stabilize projectTasks array reference
  // Only re-memoize when task content actually changes (not just array reference)
  // This prevents kanbanSections from re-memoizing when fetch returns identical data
  const stableProjectTasks = useMemo(
    () => projectTasks,
    [
      projectTasks.length,
      // Create stable hash of task IDs and statuses
      projectTasks.map((t) => `${t.id}-${t.status}`).join(","),
    ],
  );
  const [projectId, setProjectId] = useState<number | null>(null);
  const [projectData, setProjectData] = useState<RenovationProject | null>(
    null,
  );
  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<ProjectTaskStatus>(
    ProjectTaskStatus.TODO,
  );
  const [selectedPhases, setSelectedPhases] = useState<string[]>(["NONE"]); // Multi-select: default to "Geen fase"

  // Stable refs to prevent unnecessary re-fetches
  const fetchTasksAbortController = useRef<AbortController | null>(null);
  const lastFetchedProjectIdRef = useRef<number | null>(null);
  const lastFetchedPhasesRef = useRef<string[]>(["NONE"]);
  const isFetchingTasksRef = useRef(false);

  // Note: fetchProjectDetails removed - UniversalDetailPage handles this via entityConfig.api.fetchOne

  // Fetch project tasks with request deduplication
  const fetchProjectTasks = useCallback(
    async (id: number, phases: string[] = ["NONE"]) => {
      if (!session) return;

      // Prevent duplicate fetches for same project and phases
      if (isFetchingTasksRef.current) {
        console.log("⏭️ Skipping duplicate task fetch");
        return;
      }

      // Cancel any in-flight request
      if (fetchTasksAbortController.current) {
        fetchTasksAbortController.current.abort();
      }

      // Create new AbortController for this request
      fetchTasksAbortController.current = new AbortController();
      const signal = fetchTasksAbortController.current.signal;

      isFetchingTasksRef.current = true;
      setIsLoadingTasks(true);

      try {
        console.log(
          "🔄 Fetching fresh tasks for project",
          id,
          "with phase filters:",
          phases,
        );

        // Build URL with multiple phase parameters
        const url = new URL(`${API_URL}/renovation/project/${id}/tasks`);
        phases.forEach((phase) => {
          url.searchParams.append("phases", phase);
        });

        const response = await fetchWithAuth(
          url.toString(),
          {
            method: "GET",
            signal, // Pass abort signal to fetch
          },
          session,
        );

        if (response.ok) {
          const tasks = await response.json();
          console.log("Fetched project tasks:", tasks);
          console.log("First task todos:", tasks[0]?.todos);
          setProjectTasks(tasks);
        } else {
          throw new Error("Failed to fetch project tasks");
        }
      } catch (error: any) {
        // Ignore abort errors - these are intentional cancellations
        if (error.name === "AbortError") {
          console.log("Project tasks request aborted (duplicate prevented)");
          return;
        }
        console.error("Error fetching project tasks:", error);
        toast.error("Fout bij ophalen van taken");
      } finally {
        setIsLoadingTasks(false);
        isFetchingTasksRef.current = false;
      }
    },
    [session],
  );

  // Create stable refs for latest values (prevents callback re-creation)
  const projectIdRef = useRef(projectId);
  const selectedPhasesRef = useRef(selectedPhases);

  useEffect(() => {
    projectIdRef.current = projectId;
    selectedPhasesRef.current = selectedPhases;
  }, [projectId, selectedPhases]);

  // Handle add task button click - STABLE (no dependencies)
  const handleAddTaskClick = useCallback((status: ProjectTaskStatus) => {
    setSelectedStatus(status);
    setIsAddTaskModalOpen(true);
  }, []);

  // Handle task click - STABLE (no dependencies)
  const handleTaskClick = useCallback((task: ProjectTask) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  }, []);

  // Handle modal close - STABLE (no dependencies)
  const handleModalClose = useCallback(() => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
  }, []);

  // Handle modal update - STABLE (uses refs)
  const handleModalUpdate = useCallback(() => {
    const currentProjectId = projectIdRef.current;
    const currentPhases = selectedPhasesRef.current;
    if (currentProjectId) {
      fetchProjectTasks(currentProjectId, currentPhases);
    }
  }, [fetchProjectTasks]);

  // Handle phase filter toggle - STABLE (uses refs)
  const handlePhaseToggle = useCallback(
    (phase: string) => {
      setSelectedPhases((prev) => {
        const newPhases = prev.includes(phase)
          ? prev.filter((p) => p !== phase) // Remove if already selected
          : [...prev, phase]; // Add if not selected

        // Don't allow empty selection - keep at least one phase
        if (newPhases.length === 0) {
          return prev;
        }

        const currentProjectId = projectIdRef.current;
        if (currentProjectId) {
          // Reset fetch refs to allow immediate refetch with new phases
          lastFetchedPhasesRef.current = newPhases;
          fetchProjectTasks(currentProjectId, newPhases);
        }
        return newPhases;
      });
    },
    [fetchProjectTasks],
  );

  // Callback to receive entity data from UniversalDetailPage
  const handleEntityLoad = useCallback(
    async (entity: RenovationProject | null) => {
      if (entity) {
        setProjectData(entity);
      }
    },
    [],
  );

  // Set default phases when project data loads
  // Default: "Geen fase" + current project status (which maps to ProjectPhase enum)
  useEffect(() => {
    if (projectData) {
      const defaultPhases = ["NONE"]; // Always include "Geen fase" (null projectPhase)

      // Add the current project status if it exists and is not already included
      // project.status uses the same enum values as ProjectPhase
      if (projectData.status && !defaultPhases.includes(projectData.status)) {
        defaultPhases.push(projectData.status);
      }

      // Only update if current selection is different from default
      if (JSON.stringify(selectedPhases) !== JSON.stringify(defaultPhases)) {
        setSelectedPhases(defaultPhases);
      }
    }
  }, [projectData]); // Only run when projectData changes

  // Memoize navigation config separately - only depends on buildingId
  const navigationConfig = useMemo(() => {
    if (!buildingId) return RENOVATION_PROJECT_ENTITY_CONFIG.navigation;

    return {
      ...RENOVATION_PROJECT_ENTITY_CONFIG.navigation,
      backRoute: `/building/${buildingId}/renovation`,
      customNavigationHandler: (
        targetId: number,
        _searchParams: URLSearchParams,
      ) => {
        navigate(`/renovation/${targetId}?buildingId=${buildingId}`);
      },
    } as typeof RENOVATION_PROJECT_ENTITY_CONFIG.navigation;
  }, [buildingId, navigate]);

  // Create stable onTasksUpdate callback
  const handleTasksUpdate = useCallback(() => {
    const currentProjectId = projectIdRef.current;
    const currentPhases = selectedPhasesRef.current;
    if (currentProjectId) {
      fetchProjectTasks(currentProjectId, currentPhases);
    }
  }, [fetchProjectTasks]);

  // PERFORMANCE: Split memoization to prevent kanban updates from triggering photo grid re-renders
  // This is critical - when projectTasks changes, ONLY kanban should re-render, not sidebar

  // Kanban sections - depend on task-related state
  const kanbanSections = useMemo(
    () => [
      {
        id: "project-tasks-kanban",
        position: "after-content" as const,
        component: ProjectTasksKanbanSection,
        componentProps: {
          projectId,
          projectTasks: stableProjectTasks, // Use stable reference
          isLoadingTasks,
          onTasksUpdate: handleTasksUpdate,
          onTaskClick: handleTaskClick,
          onAddClick: handleAddTaskClick,
          selectedPhases,
          onPhaseToggle: handlePhaseToggle,
          projectStatus: projectData?.status,
        },
      },
    ],
    [
      projectId,
      stableProjectTasks, // Depends on stable reference, not raw array
      isLoadingTasks,
      selectedPhases,
      projectData?.status,
      handleTasksUpdate,
      handleTaskClick,
      handleAddTaskClick,
      handlePhaseToggle,
    ],
  );

  // Sidebar removed — photos and documents are now rendered as full-width
  // sections below the kanban so it can use the entire available space.
  const sidebarSections = useMemo(() => [], []);

  // Other sections - task-independent content sections
  const otherSections = useMemo(
    () => [
      // Budget lock + comparison chart (initieel vs werkelijk vs gefactureerd)
      {
        id: "project-budget-lock",
        position: "after-content" as const,
        component: withLazySuspense(BudgetLockSection),
        componentProps: {},
      },

      // Project Photos (moved from sidebar to full-width below kanban)
      {
        id: "project-photos",
        position: "after-content" as const,
        component: withLazySuspense(UnifiedProjectPhotosSection),
        componentProps: {},
      },

      // Project Notes Section
      {
        id: "project-notes",
        position: "after-content" as const,
        component: withLazySuspense(ProjectNotesSection),
        componentProps: {},
      },

      // Financial Documents Section — embedded subset of /renovation/documents
      // scoped to this project.  Reuses the full RenovationDocumentsList in
      // its `embedded` mode so users get the same stats / pagination /
      // filtering / kostenplaatsen UX without a page-level header.
      // (Replaces the old Project offertes/bestelbonnen/facturen and the
      // generic Documenten + Project e-mails sections, which had no remaining
      // use cases.)
      {
        id: "project-financial-documents",
        position: "after-content" as const,
        component: withLazySuspense(ProjectFinancialDocumentsSection),
        componentProps: {},
      },
    ],
    [], // Static sections - never change
  );

  // Project parts section - rendered above the project tasks kanban so it sits
  // closer to the project details and gets the most attention. The planning
  // lock banner sits just above so the user can review and approve the
  // auto-derived planning before locking it.
  const projectPartsSections = useMemo(
    () => [
      {
        id: "planning-lock",
        position: "after-content" as const,
        component: withLazySuspense(PlanningLockSection),
        componentProps: {},
      },
      {
        id: "project-units-overview",
        position: "after-content" as const,
        component: withLazySuspense(ProjectUnitsOverviewSection),
        componentProps: {},
      },
      {
        id: "project-parts",
        position: "after-content" as const,
        component: withLazySuspense(ProjectPartsSection),
        componentProps: {},
      },
    ],
    [],
  );

  // Combine all sections (cheap array concatenation).
  // Order: project parts → project tasks kanban → sidebar → other content.
  const customSections = useMemo(
    () => [
      ...projectPartsSections,
      ...kanbanSections,
      ...sidebarSections,
      ...otherSections,
    ],
    [projectPartsSections, kanbanSections, sidebarSections, otherSections],
  );

  // Combine all config parts into final enhanced config
  const enhancedConfig = useMemo(
    () => ({
      ...RENOVATION_PROJECT_ENTITY_CONFIG,
      navigation: navigationConfig,
      plugins: {
        ...RENOVATION_PROJECT_ENTITY_CONFIG.plugins,
        customSections, // Custom sections must be inside plugins, not at root level
        onEntityLoad: handleEntityLoad,
      },
    }),
    [navigationConfig, customSections, handleEntityLoad],
  );

  // Extract project ID from URL - UniversalDetailPage fetches the project data
  useEffect(() => {
    const pathParts = window.location.pathname.split("/");
    const idFromUrl = pathParts[pathParts.indexOf("renovation") + 1];

    if (idFromUrl && !isNaN(Number(idFromUrl))) {
      const id = Number(idFromUrl);
      setProjectId(id);

      // Only fetch tasks when we have session and project ID
      // AND only if we haven't fetched this project yet OR phases changed
      const phasesChanged =
        JSON.stringify(lastFetchedPhasesRef.current) !==
        JSON.stringify(selectedPhases);
      if (
        session &&
        id &&
        (lastFetchedProjectIdRef.current !== id || phasesChanged)
      ) {
        lastFetchedProjectIdRef.current = id;
        lastFetchedPhasesRef.current = selectedPhases;
        fetchProjectTasks(id, selectedPhases);
      }
    }
  }, [session, selectedPhases, fetchProjectTasks]); // Stable dependencies

  // Cleanup: abort in-flight requests on unmount
  useEffect(() => {
    return () => {
      if (fetchTasksAbortController.current) {
        fetchTasksAbortController.current.abort();
        console.log("Cleanup: aborted in-flight project tasks request");
      }
    };
  }, []);

  return (
    <ProjectPartsProvider projectId={projectId}>
      <UniversalDetailPage
        entityConfig={enhancedConfig}
        className="renovation-project-detail"
      />

      {/* Project Task Detail Modal */}
      <ProjectTaskDetailModal
        task={selectedTask}
        isOpen={isTaskModalOpen}
        onClose={handleModalClose}
        onUpdate={handleModalUpdate}
      />

      {/* Add Project Task Modal */}
      <AddProjectTaskModal
        isOpen={isAddTaskModalOpen}
        onClose={() => setIsAddTaskModalOpen(false)}
        onSuccess={() => {
          setIsAddTaskModalOpen(false);
          if (projectId) {
            fetchProjectTasks(projectId, selectedPhases);
          }
        }}
        projectId={projectId || 0}
        defaultStatus={selectedStatus}
        defaultAssigneeId={projectData?.responsibleUserId}
        projectStatus={projectData?.status}
      />

      {/* Full-Screen Photos Modal - Rendered via Portal */}
      <PhotosModal
        isOpen={showPhotosModal}
        onClose={() => setShowPhotosModal(false)}
        projectId={projectId || 0}
      />
    </ProjectPartsProvider>
  );
};

export default RenovationProjectDetailPage;
